<script lang="ts">
	import type { Game } from '../lib/catalog'
	import { SHOW_META } from '../lib/config'

	let {
		game,
		selected,
		onpick,
	}: { game: Game; selected: boolean; onpick: (slug: string) => void } = $props()
</script>

<!--
	A button rather than a clickable div: picking a game is the grid's only
	interaction, and it should reach the keyboard without a roving tabindex.
-->
<button
	class="card"
	class:selected
	type="button"
	aria-pressed={selected}
	onclick={() => onpick(game.slug)}
>
	<!-- The back office sets the banner; without one, the striped stand-in. -->
	<div class="art">
		{#if game.banner}
			<img src={game.banner} alt="" loading="lazy" />
		{:else}
			<span>art / {game.slug}</span>
		{/if}
	</div>

	<div class="body">
		<span class="name">{game.name}</span>
		<span class="studio">{game.studio}</span>

		{#if SHOW_META}
			<div class="meta">
				<!-- A prototype has no RTP to quote, so it wears one chip saying so. -->
				{#if game.type !== 'prototype'}
					<span class="chip">RTP {game.rtp}</span>
				{/if}
				<span class="chip">{game.vol}</span>
			</div>
		{/if}
	</div>

	<!--
		The selection ring is its own layer so it can sit over the art without the
		card's border-box shifting its contents by two pixels when it appears.
	-->
	<span class="ring"></span>
</button>

<style>
	.card {
		position: relative;
		display: block;
		width: 100%;
		padding: 0;
		border: 1px solid var(--line);
		border-radius: 8px;
		background: var(--card);
		color: inherit;
		font: inherit;
		text-align: left;
		cursor: pointer;
		overflow: hidden;
	}

	.card:hover {
		border-color: var(--line-hover);
	}

	.card:focus-visible {
		border-color: var(--accent);
	}

	.art {
		position: relative;
		display: flex;
		align-items: flex-end;
		height: 116px;
		padding: 9px;
		background-color: var(--art);
		background-image: repeating-linear-gradient(
			135deg,
			var(--art-stripe) 0 6px,
			var(--art) 6px 12px
		);
	}

	.art span {
		color: var(--faint);
		font-size: 9px;
		letter-spacing: 0.06em;
	}

	/*
		Covers the box rather than fitting it: the columns stretch on wide
		screens, so the 16:9 the banner is authored at is a target, not a
		guarantee.
	*/
	.art img {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.body {
		display: flex;
		flex-direction: column;
		gap: 4px;
		padding: 11px 12px 13px;
	}

	.name {
		font-size: 12px;
		font-weight: 700;
		color: var(--text);
	}

	.studio {
		font-size: 10px;
		color: var(--dim);
	}

	.meta {
		display: flex;
		gap: 6px;
		margin-top: 5px;
	}

	.chip {
		padding: 2px 5px;
		border: 1px solid var(--line-strong);
		border-radius: 3px;
		color: var(--muted);
		font-size: 9px;
	}

	.ring {
		position: absolute;
		inset: 0;
		border: 2px solid transparent;
		border-radius: 8px;
		pointer-events: none;
	}

	.card.selected .ring {
		border-color: var(--accent);
	}
</style>
